import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Truck, RotateCcw, Boxes, PackageX, AlertTriangle, PackageCheck,
  Clock, Building2, ArrowRight, Activity as ActivityIcon, ChevronRight, Store,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { ListSkeleton, CardSkeleton } from "@/components/feedback/Skeletons";
import { useAuth } from "@/hooks/useAuth";
import { canWrite } from "@/lib/permissions/permissions";
import { dashboardService } from "@/services/dashboardService";
import { greeting, formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const KPIS = [
  { key: "ready_orders", label: "Ready Orders", icon: PackageCheck, tone: "green", to: "/orders?status=ready" },
  { key: "pending_orders", label: "Pending Orders", icon: Clock, tone: "neutral", to: "/orders?status=pending" },
  { key: "shipped_today", label: "Shipped Today", icon: ShoppingCart, tone: "blue", to: "/orders?status=shipped" },
  { key: "inbound_waiting", label: "Inbound Waiting", icon: Truck, tone: "blue", to: "/inbound?status=expected" },
  { key: "returns_pending", label: "Returns Pending", icon: RotateCcw, tone: "amber", to: "/returns?status=pending" },
  { key: "low_stock", label: "Low Stock", icon: AlertTriangle, tone: "amber", to: "/inventory?status=low" },
  { key: "out_of_stock", label: "Out of Stock", icon: PackageX, tone: "red", to: "/inventory?status=out" },
  { key: "quantity_problems", label: "Quantity Problems", icon: AlertTriangle, tone: "red", to: "/orders?status=quantity_problem" },
];

const CATEGORY_META = {
  orders: { icon: ShoppingCart, tone: "bg-brand-light text-primary" },
  inbound: { icon: Truck, tone: "bg-warning-light text-warning-dark" },
  returns: { icon: RotateCcw, tone: "bg-success-light text-success-dark" },
  inventory: { icon: Boxes, tone: "bg-error-light text-error-dark" },
  clients: { icon: Building2, tone: "bg-brand-light text-primary" },
  stores: { icon: Store, tone: "bg-brand-light text-primary" },
  invoicing: { icon: ShoppingCart, tone: "bg-muted text-muted-foreground" },
};

const QUICK_ACTIONS = [
  { key: "order", label: "New Order", icon: ShoppingCart, to: "/orders?new=1", module: "orders" },
  { key: "inbound", label: "New Inbound", icon: Truck, to: "/inbound?new=1", module: "inbound" },
  { key: "return", label: "New Return", icon: RotateCcw, to: "/returns?new=1", module: "returns" },
  { key: "client", label: "New Client", icon: Building2, to: "/clients?new=1", module: "clients" },
];

function TrendChart({ days }) {
  if (!days || days.length === 0) return null;
  const w = 640;
  const h = 200;
  const pad = 32;
  const max = Math.max(1, ...days.map((d) => Math.max(d.shipped, d.received)));
  const x = (i) => pad + (i * (w - pad * 2)) / (days.length - 1 || 1);
  const y = (v) => h - pad - (v / max) * (h - pad * 2);
  const line = (key) => days.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d[key])}`).join(" ");
  const area = (key) => {
    const path = line(key);
    return `${path} L${x(days.length - 1)},${h - pad} L${x(0)},${h - pad} Z`;
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-52 w-full" data-testid="dashboard-trend-chart">
      <defs>
        <linearGradient id="shippedFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="receivedFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="shippedStroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(199 89% 60%)" />
        </linearGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <line
          key={f}
          x1={pad}
          x2={w - pad}
          y1={pad + f * (h - pad * 2)}
          y2={pad + f * (h - pad * 2)}
          className="stroke-border"
          strokeWidth="1"
          strokeDasharray={f === 1 ? "0" : "3 6"}
          opacity={f === 0 || f === 1 ? 0.6 : 0.3}
        />
      ))}
      <path d={area("received")} fill="url(#receivedFill)" />
      <path d={area("shipped")} fill="url(#shippedFill)" />
      <path
        d={line("received")}
        fill="none"
        stroke="#F59E0B"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="5 4"
        opacity="0.9"
      />
      <path
        d={line("shipped")}
        fill="none"
        stroke="url(#shippedStroke)"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        filter="url(#glow)"
        style={{ strokeDasharray: 1000, strokeDashoffset: 0, animation: "trendDraw 1.4s cubic-bezier(0.2,0.8,0.2,1) both" }}
      />
      {days.map((d, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(d.received)} r="3" fill="#F59E0B" opacity="0.85" />
          <circle cx={x(i)} cy={y(d.shipped)} r="4" fill="hsl(var(--primary))" />
          <circle cx={x(i)} cy={y(d.shipped)} r="8" fill="hsl(var(--primary))" opacity="0.18" />
          <text x={x(i)} y={h - 10} textAnchor="middle" className="fill-muted-foreground text-[9px]">{d.date}</text>
        </g>
      ))}
      <style>{`@keyframes trendDraw { from { stroke-dasharray: 1000; stroke-dashoffset: 1000; } to { stroke-dasharray: 1000; stroke-dashoffset: 0; } }`}</style>
    </svg>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firstName = (user?.name || "").split(" ")[0];

  const summaryQuery = useQuery({ queryKey: ["dashboard", "summary"], queryFn: dashboardService.summary });
  const trendsQuery = useQuery({ queryKey: ["dashboard", "trends"], queryFn: dashboardService.trends });
  const activityQuery = useQuery({ queryKey: ["dashboard", "activity"], queryFn: () => dashboardService.activity(6) });

  const counts = summaryQuery.data?.counts || {};
  const todaysWork = (summaryQuery.data?.todays_work || []).filter((t) => t.count > 0);
  const lowItems = summaryQuery.data?.low_stock_items || [];
  const outItems = summaryQuery.data?.out_of_stock_items || [];
  const quickActions = QUICK_ACTIONS.filter((a) => canWrite(user?.role, a.module));

  return (
    <div className="space-y-8" data-testid="page-dashboard">
      <PageHeader
        title={`${greeting()}${firstName ? `, ${firstName}` : ""}`}
        description="Here's what needs your attention today."
      />

      {/* Today's Work */}
      <section data-testid="todays-work">
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <p className="overline">Today's Focus</p>
            <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Today's Work</h2>
          </div>
        </div>
        {summaryQuery.isLoading ? (
          <ListSkeleton rows={3} />
        ) : todaysWork.length === 0 ? (
          <div className="rounded-2xl border border-border/60 bg-card/80 p-6 text-sm text-muted-foreground shadow-premium backdrop-blur-sm" data-testid="todays-work-empty">
            Nothing needs attention right now. You're all caught up. 🎉
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {todaysWork.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => navigate(t.link + (t.filter ? `?status=${t.filter}` : ""))}
                data-testid={`todays-work-${t.key}`}
                className="group flex items-center justify-between rounded-2xl border border-border/60 bg-card/80 p-4 text-left shadow-premium backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40"
              >
                <div className="flex items-center gap-3">
                  <span className="font-display flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-lg font-semibold text-primary ring-1 ring-inset ring-primary/20">{t.count}</span>
                  <span className="text-sm font-medium text-foreground">{t.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* KPI cards */}
      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <p className="overline">Key Metrics</p>
            <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Operations Snapshot</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {summaryQuery.isLoading
            ? Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
            : KPIS.map((k) => (
                <StatCard
                  key={k.key}
                  testId={`kpi-${k.key}`}
                  label={k.label}
                  value={counts[k.key] ?? 0}
                  icon={k.icon}
                  tone={k.tone}
                  onClick={() => navigate(k.to)}
                />
              ))}
        </div>
      </section>

      {/* Inventory Alerts */}
      <section data-testid="inventory-alerts">
        <div className="mb-4">
          <p className="overline">Stock Health</p>
          <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Inventory Alerts</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AlertList title="Low stock" tone="amber" items={lowItems} onNavigate={() => navigate("/inventory?status=low")} testId="alert-low" />
          <AlertList title="Out of stock" tone="red" items={outItems} onNavigate={() => navigate("/inventory?status=out")} testId="alert-out" />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <section className="lg:col-span-2" data-testid="recent-activity">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <p className="overline">Live Stream</p>
              <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Recent Activity</h2>
            </div>
            <button type="button" onClick={() => navigate("/activity-log")}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-brand-dark" data-testid="view-activity-log">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {activityQuery.isLoading ? (
            <ListSkeleton rows={5} />
          ) : activityQuery.isError ? (
            <ErrorState onRetry={() => activityQuery.refetch()} />
          ) : activityQuery.data?.items?.length ? (
            <div className="divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-premium backdrop-blur-sm">
              {activityQuery.data.items.map((item) => {
                const meta = CATEGORY_META[item.category] || { icon: ActivityIcon, tone: "bg-muted text-muted-foreground" };
                const Icon = meta.icon;
                return (
                  <div key={item.id} className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-muted/40" data-testid={`activity-${item.id}`}>
                    <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", meta.tone)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">
                        <span className="font-medium">{item.actor}</span>{" "}
                        <span className="text-muted-foreground">{item.action}</span>{" "}
                        <span className="font-medium">{item.target}</span>
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeTime(item.created_at)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={ActivityIcon} title="No recent activity" description="Activity from across your workspace will appear here." />
          )}
        </section>

        {/* Quick Actions */}
        <section data-testid="quick-actions">
          <div className="mb-4">
            <p className="overline">Shortcuts</p>
            <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {quickActions.map(({ key, label, icon: Icon, to }) => (
              <button key={key} type="button" onClick={() => navigate(to)} data-testid={`quick-action-${key}`}
                className="group flex items-center gap-3 rounded-2xl border border-border/60 bg-card/80 p-4 text-left shadow-premium backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="flex-1 text-sm font-semibold text-foreground">{label}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Trend chart */}
      <section data-testid="dashboard-trends">
        <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-premium backdrop-blur-sm">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <p className="overline">Throughput</p>
              <h2 className="font-display mt-1 text-xl font-semibold tracking-tight text-foreground">Last 7 Days</h2>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground"><span className="h-2 w-4 rounded-full bg-primary" /> Shipped</span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground"><span className="h-2 w-4 rounded-full bg-warning" /> Received</span>
            </div>
          </div>
          {trendsQuery.isLoading ? <div className="h-44" /> : <TrendChart days={trendsQuery.data?.days} />}
        </div>
      </section>
    </div>
  );
}

function AlertList({ title, tone, items, onNavigate, testId }) {
  const toneCls = tone === "red" ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400";
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-premium backdrop-blur-sm" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-border/60 px-5 py-3.5">
        <span className={cn("font-display text-sm font-semibold", toneCls)}>{title}</span>
        <button type="button" onClick={onNavigate} className="text-xs font-medium text-primary transition-colors hover:text-primary/80">View all</button>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-muted-foreground">No items.</div>
      ) : (
        <div className="divide-y divide-border/60">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between px-5 py-2.5" data-testid={`${testId}-item`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{it.sku}</p>
                <p className="truncate text-xs text-muted-foreground">{it.product_name || "—"}</p>
              </div>
              <span className={cn("font-display text-base font-semibold tabular-nums", toneCls)}>{it.available}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
